import { useEffect, useRef } from "react";
import { professional } from "@/config/professional";

export default function Hero() {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) {
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";
      return;
    }

    el.style.opacity = "0";
    el.style.transform = "translateY(16px)";
    el.style.transition = "opacity 0.9s ease-out, transform 0.9s ease-out";

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      });
    });

    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <section
      id="inicio"
      className="relative flex min-h-[80svh] items-center overflow-hidden bg-primary-900 md:min-h-[85svh]"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={professional.heroImage}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover object-top"
        />
        {/* Green tinted veil from the brand palette */}
        <div className="absolute inset-0 bg-primary-600/25" />
        {/* Lateral gradient: darker on left where text sits, transparent toward right */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/80 via-primary-900/45 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/50 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 py-24 md:px-6 lg:px-8">
        <div
          ref={contentRef}
          className="max-w-lg md:max-w-xl lg:max-w-2xl"
          style={{ opacity: 0, transform: "translateY(16px)" }}
        >
          {/* Professional identification */}
          <p className="mt-1.5 text-[11px] font-medium uppercase tracking-[0.25em] text-background-100/60">
            Enfermeira Estomaterapeuta
          </p>

          {/* Headline */}
          <h1 className="mt-8 font-heading text-[2.1rem] font-semibold leading-[1.12] tracking-tight text-background-50 sm:text-[2.6rem] md:mt-10 md:text-[3.2rem] lg:text-[3.5rem]">
            Cuidado especializado para cicatrizar, aliviar a dor e devolver
            qualidade de vida.
          </h1>

          {/* Subheadline */}
          <p className="mt-6 max-w-md text-[0.95rem] leading-[1.65] text-background-100/80 md:mt-8 md:max-w-lg md:text-base">
            Estomaterapia e Laserterapia com atendimento individualizado, humano
            e baseado em ciência.
          </p>

          {/* CTA */}
          <div className="mt-9 md:mt-10">
            <a
              href={professional.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-full bg-accent-400 px-8 py-3.5 text-sm font-semibold text-primary-950 transition-all duration-300 hover:bg-accent-300 md:px-9 md:py-4 md:text-base"
            >
              {professional.cta.primary}
              <i
                className="ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1"
                aria-hidden="true"
              ></i>
            </a>
          </div>

        </div>
      </div>

      {/* Bottom fade to page background */}
      <div className="absolute bottom-0 left-0 h-20 w-full bg-gradient-to-t from-background-50 to-transparent md:h-24" />
    </section>
  );
}